import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import databaseService from '../services/databaseService';
import initService from '../services/initService';
import { isAuthenticated } from '../services/authService';
import RichTextEditor from './RichTextEditor';
import './Admin.css';

function Admin() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [archives, setArchives] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [editingArchive, setEditingArchive] = useState(null);
  const [showNewPost, setShowNewPost] = useState(false);
  const [showNewArchive, setShowNewArchive] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    if (!isAuthenticated()) {
      window.location.href = '/auth/login';
      return;
    }
    
    loadData();
  }, []);

  const loadData = async () => {
    try {
      if (initService.isDatabaseAvailable()) {
        console.log('Loading data from database...');
        try {
          const [postsData, archivesData] = await Promise.all([
            databaseService.getAllPosts(),
            databaseService.getAllArchives()
          ]);
          setPosts(postsData || []);
          setArchives(archivesData || []);
          setJournalEntries([]);
        } catch (error) {
          console.warn('Database query failed, using fallback:', error);
          loadFallbackData();
        }
      } else {
        console.log('Database not available, using fallback data...');
        loadFallbackData();
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      loadFallbackData();
    } finally {
      setLoading(false);
    }
  };

  const loadFallbackData = () => {
    // Create some sample data
    const samplePosts = [
      {
        id: 1,
        title: "About",
        route: "/about",
        description: "About page content",
        created_at: new Date().toISOString()
      },
      {
        id: 2,
        title: "Advice",
        route: "/advice", 
        description: "Advice page content",
        created_at: new Date().toISOString()
      }
    ];
    
    const sampleArchives = [
      {
        id: 1,
        title: "Sample Archive Post",
        route: "/archives/sample",
        excerpt: "This is a sample archive post",
        date: "2024-01-01",
        created_at: new Date().toISOString()
      }
    ];
    
    setPosts(samplePosts);
    setArchives(sampleArchives);
    setJournalEntries([]);
  };

  const handleEditPost = (post) => {
    navigate(`/admin/edit/post/${post.id}`);
  };

  const handleEditArchive = (archive) => {
    navigate(`/admin/edit/archive/${archive.id}`);
  };

  const handleEditJournal = (entry) => {
    navigate(`/admin/edit/journal/${entry.id}`);
  };

  const handleDeleteJournal = async (id) => {
    if (window.confirm('Are you sure you want to delete this journal entry?')) {
      try {
        await databaseService.deleteJournalEntry(id);
        loadData();
      } catch (error) {
        console.error('Failed to delete journal entry:', error);
      }
    }
  };

  // Pagination logic
  const getCurrentItems = () => {
    const items = activeTab === 'posts' ? posts : archives;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const items = activeTab === 'posts' ? posts : archives;
    return Math.ceil(items.length / itemsPerPage);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page when changing tabs
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleSavePost = async (postData) => {
    try {
      if (editingPost) {
        // Update existing post
        await databaseService.updatePost(editingPost.id, {
          ...editingPost,
          ...postData,
          updated_at: new Date().toISOString()
        });
      } else {
        // Create new post
        const newPost = {
          ...postData,
          route: generateRoute(postData.title),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        await databaseService.createPost(newPost);
      }
      
      await loadData();
      setEditingPost(null);
      setShowNewPost(false);
    } catch (error) {
      console.error('Failed to save post:', error);
      alert('Failed to save post. Please try again.');
    }
  };

  const handleSaveArchive = async (archiveData) => {
    try {
      if (editingArchive) {
        // Update existing archive
        await databaseService.updateArchivePost(editingArchive.id, {
          ...editingArchive,
          ...archiveData,
          updated_at: new Date().toISOString()
        });
      } else {
        // Create new archive
        const newArchive = {
          ...archiveData,
          route: generateRoute(archiveData.title),
          year: new Date().getFullYear(),
          month: new Date().getMonth() + 1,
          date: new Date().toISOString().split('T')[0],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        await databaseService.createArchivePost(newArchive);
      }
      
      await loadData();
      setEditingArchive(null);
      setShowNewArchive(false);
    } catch (error) {
      console.error('Failed to save archive:', error);
      alert('Failed to save archive. Please try again.');
    }
  };

  const generateRoute = (title) => {
    return '/' + title.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await databaseService.deletePost(postId);
        await loadData();
      } catch (error) {
        console.error('Failed to delete post:', error);
        alert('Failed to delete post. Please try again.');
      }
    }
  };

  const handleDeleteArchive = async (archiveId) => {
    if (window.confirm('Are you sure you want to delete this archive post?')) {
      try {
        await databaseService.deleteArchivePost(archiveId);
        await loadData();
      } catch (error) {
        console.error('Failed to delete archive:', error);
        alert('Failed to delete archive. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    setEditingPost(null);
    setEditingArchive(null);
    setShowNewPost(false);
    setShowNewArchive(false);
  };

  if (loading) {
    return <div className="admin-container">Loading...</div>;
  }

  // Show editor if editing or creating
  if (editingPost || showNewPost) {
    return (
      <div className="admin-container">
        <RichTextEditor
          initialContent={editingPost?.content || ''}
          title={editingPost?.title || ''}
          onSave={handleSavePost}
          onCancel={handleCancel}
          isEditing={!!editingPost}
        />
      </div>
    );
  }

  if (editingArchive || showNewArchive) {
    return (
      <div className="admin-container">
        <RichTextEditor
          initialContent={editingArchive?.content || ''}
          title={editingArchive?.title || ''}
          onSave={handleSaveArchive}
          onCancel={handleCancel}
          isEditing={!!editingArchive}
        />
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Blog Admin Panel</h1>
        <div className="admin-actions-header">
          <button 
            className="btn btn-primary"
            onClick={() => setShowNewPost(true)}
          >
            + New Post
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowNewArchive(true)}
          >
            + New Archive
          </button>
        </div>
      </div>
      
      <div className="admin-tabs">
        <button 
          className={activeTab === 'posts' ? 'active' : ''}
          onClick={() => handleTabChange('posts')}
        >
          Posts ({posts.length})
        </button>
        <button 
          className={activeTab === 'archives' ? 'active' : ''}
          onClick={() => handleTabChange('archives')}
        >
          Archives ({archives.length})
        </button>
        {/* Journal tab temporarily disabled */}
        {/* <button 
          className={activeTab === 'journal' ? 'active' : ''}
          onClick={() => handleTabChange('journal')}
        >
          Journal ({journalEntries.length})
        </button> */}
      </div>
      
      {activeTab === 'posts' && (
        <div className="admin-section">
          <h2>Blog Posts</h2>
          <div className="admin-list">
            {getCurrentItems().map((post, index) => (
              <div key={index} className="admin-item">
                <div className="admin-item-content">
                  <h3>{post.title}</h3>
                  <p className="admin-route">{post.route}</p>
                  <p className="admin-excerpt">{post.excerpt || post.description}</p>
                  <p className="admin-date">
                    Created: {post.created_at ? new Date(post.created_at).toLocaleDateString() : 'Unknown'}
                    {post.updated_at && post.updated_at !== post.created_at && 
                      ` | Updated: ${new Date(post.updated_at).toLocaleDateString()}`
                    }
                  </p>
                </div>
                <div className="admin-actions">
                  <button 
                    className="btn btn-small btn-primary"
                    onClick={() => handleEditPost(post)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-small btn-danger"
                    onClick={() => handleDeletePost(post.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
          {getTotalPages() > 1 && (
            <div className="pagination">
              <button 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn btn-secondary"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {getTotalPages()}
              </span>
              <button 
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === getTotalPages()}
                className="btn btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'archives' && (
        <div className="admin-section">
          <h2>Archive Posts</h2>
          <div className="admin-list">
            {getCurrentItems().map((archive, index) => (
              <div key={index} className="admin-item">
                <div className="admin-item-content">
                  <h3>{archive.title}</h3>
                  <p className="admin-route">{archive.route}</p>
                  <p className="admin-excerpt">{archive.excerpt}</p>
                  <p className="admin-date">
                    {archive.date} | Created: {archive.created_at ? new Date(archive.created_at).toLocaleDateString() : 'Unknown'}
                    {archive.updated_at && archive.updated_at !== archive.created_at && 
                      ` | Updated: ${new Date(archive.updated_at).toLocaleDateString()}`
                    }
                  </p>
                </div>
                <div className="admin-actions">
                  <button 
                    className="btn btn-small btn-primary"
                    onClick={() => handleEditArchive(archive)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-small btn-danger"
                    onClick={() => handleDeleteArchive(archive.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
          {getTotalPages() > 1 && (
            <div className="pagination">
              <button 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn btn-secondary"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {getTotalPages()}
              </span>
              <button 
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === getTotalPages()}
                className="btn btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Journal section temporarily disabled */}
      {/* {activeTab === 'journal' && (
        <div className="admin-section">
          <h2>Journal Entries</h2>
          <div className="admin-list">
            {getCurrentItems().map((entry, index) => (
              <div key={index} className="admin-item">
                <div className="admin-item-content">
                  <h3>{entry.title}</h3>
                  <p className="admin-excerpt">{entry.excerpt}</p>
                  <p className="admin-date">
                    Created: {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : 'Unknown'}
                    {entry.updated_at && entry.updated_at !== entry.created_at && 
                      ` | Updated: ${new Date(entry.updated_at).toLocaleDateString()}`
                    }
                  </p>
                </div>
                <div className="admin-actions">
                  <button 
                    className="btn btn-small btn-primary"
                    onClick={() => handleEditJournal(entry)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-small btn-danger"
                    onClick={() => handleDeleteJournal(entry.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
          {getTotalPages() > 1 && (
            <div className="pagination">
              <button 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn btn-secondary"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {getTotalPages()}
              </span>
              <button 
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === getTotalPages()}
                className="btn btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )} */}
    </div>
  );
}

export default Admin;